import React, { useEffect, useState, useMemo } from "react";
import { MdChevronRight, MdChevronLeft } from 'react-icons/md';
import {
    Button,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Icon,
    Stack,
    Avatar,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    Box,
    Spinner,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { useParams } from "react-router-dom";
import { useDiscount } from "contexts/DiscountContext";
import Card from "components/card/Card";
import { BiDetail } from "react-icons/bi";
import Pagination from "components/Pagination";

const ReceiptCell = ({ imageUrl, name }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedImage, setSelectedImage] = useState(null);

    const handleAvatarClick = () => {
        setSelectedImage(imageUrl || '');
        onOpen();
    };

    return (
        <>
            <Flex align="center">
                <Avatar
                    h="48px"
                    w="48px"
                    bg="#fa8f03"
                    color="white"
                    icon={<BiDetail />}
                    src={imageUrl ? imageUrl : null}
                    cursor="pointer"
                    onClick={handleAvatarClick}
                />
            </Flex>

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalCloseButton
                        bg="#fa8f03"
                        color="white"
                        borderRadius='50%'
                        _hover={{ bg: "#d57c02" }}
                        _active={{ bg: "#d57c02" }}
                    />
                    <ModalBody
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        p={0}
                        minHeight="300px"
                        minWidth="300px"
                    >
                        {selectedImage ? (
                            <img
                                src={selectedImage}
                                alt="Selected"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    maxWidth: '400px',
                                    maxHeight: '400px'
                                }}
                            />
                        ) : (
                            <Avatar
                                h="200px"
                                w="200px"
                                bg="#fa8f03"
                                color="white"
                                icon={<BiDetail />}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default function DiscountClaim() {
    const { getDiscountClaimDetails, getSingleDiscount } = useDiscount()

    const params = useParams();

    const textColor = useColorModeValue('navy.700', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1
    });
    const [discountName, setDiscountName] = useState([]);

    useEffect(() => {
        fetchDiscountClaimDetail();
    }, [params.id, filter]);

    const fetchDiscountClaimDetail = async () => {
        if (params.id) {
            setIsLoading(true);
            try {
                const discount = await getSingleDiscount(params.id);
                if (discount) {
                    setDiscountName(discount.name);
                }

                const discountClaim = await getDiscountClaimDetails(params.id, filter);
                if (discountClaim && discountClaim.discountDetails.length > 0) {
                    setData(discountClaim.discountDetails);
                    setTotalPages(discountClaim.totalPages || 0)
                }
            } catch (error) {
                setData([]);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('userName', {
            header: () => <Text color="gray.400">USER NAME</Text>,
            cell: (info) => (
                <Flex align="center">
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                </Flex>
            ),
        }),
        columnHelper.accessor('amount', {
            header: () => <Text color="gray.400">AMOUNT</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {`€${info.getValue()}`}
                </Text>
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: () => <Text color="gray.400">DATE</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('receipt', {
            header: () => <Text color="gray.400">RECEIPTS</Text>,
            cell: (info) => {
                const name = info.row.original.userName || '';

                const imageUrl = info.getValue();
                return imageUrl ?
                    (
                        <ReceiptCell imageUrl={imageUrl} name={name} />)
                    :
                    (
                        <Text color={textColor} fontSize="md" fontWeight="500" ms='15px'>
                            -
                        </Text>
                    )
            },
        }),
    ], []);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    return (
        <Flex direction='column' pt={{ sm: "125px", lg: "45px" }}>
            <Card px='0px' minHeight="80vh">
                <Flex direction="column" w="100%" overflowX="auto">
                    <Flex align="center" justify="space-between" w="100%" px="22px" mb="36px">
                        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                            {discountName}
                        </Text>
                    </Flex>
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="10px"
                            overflow="hidden"
                            minHeight="500px"
                        >
                            <Spinner color="#fa8f03" />
                        </Box>
                    ) : (
                        <>
                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="500px">
                                <Table variant="simple">
                                    <Thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                {headerGroup.headers.map((header) => (
                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                        {header.isPlaceholder ? null : (
                                                            <Flex
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                justify="space-between"
                                                                align="center"
                                                                color="white"
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </Flex>
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody>
                                        {data.length > 0 ? (
                                            table.getRowModel().rows.map((row) => (
                                                <Tr key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={columns.length} textAlign="center">
                                                    No discount claims found.
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>
                            <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                        </>
                    )}
                </Flex>
            </Card>
        </Flex>
    );
}   
